/**
 * TanStack Query Client Configuration
 *
 * Centralized query client with optimized defaults for the Benefits Outreach 360 app.
 *
 * Key features:
 * - 5-minute stale time (reduces unnecessary refetches)
 * - 30-minute garbage collection time
 * - Exponential backoff for retries
 * - Window focus refetch disabled (SSE handles real-time updates)
 * - Single mutation retry to prevent duplicate operations
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default options for query behavior across the app
 */
const queryDefaults = {
  /**
   * Time before cached data is considered stale
   * 5 minutes works well with our SSE real-time updates
   */
  staleTime: 5 * 60 * 1000,

  /**
   * Time before unused cached data is garbage collected
   * 30 minutes allows returning to tabs without refetch
   */
  gcTime: 30 * 60 * 1000,

  /**
   * Retry failed queries with exponential backoff
   * Max 3 retries: 1s, 2s, 4s delays
   */
  retry: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),

  /**
   * Disable refetch on window focus
   * Our SSE connection (useCacheSync) handles real-time updates
   * This prevents unnecessary API calls when switching tabs
   */
  refetchOnWindowFocus: false,

  /**
   * Disable refetch on reconnect
   * SSE will push updates when connection is restored
   */
  refetchOnReconnect: false,

  /**
   * Keep refetch on mount for fresh data when navigating
   */
  refetchOnMount: true,
};

/**
 * Default options for mutations
 */
const mutationDefaults = {
  /**
   * Single retry for mutations to prevent duplicate operations
   * Most mutations are idempotent but we stay conservative
   */
  retry: 1,
};

/**
 * Create the query client instance
 *
 * Usage:
 * - Import this in providers.tsx
 * - Wrap app with QueryClientProvider
 * - Access via useQueryClient() hook in components
 *
 * @example
 * ```tsx
 * import { queryClient } from '@shared/lib/queryClient';
 *
 * <QueryClientProvider client={queryClient}>
 *   <App />
 * </QueryClientProvider>
 * ```
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: queryDefaults,
    mutations: mutationDefaults,
  },
});

/**
 * Helper to invalidate all queries for a specific entity
 *
 * @example
 * ```tsx
 * // After a mutation that affects campaigns
 * invalidateEntity('campaigns');
 * ```
 */
export const invalidateEntity = (entity: string) => {
  queryClient.invalidateQueries({ queryKey: [entity] });
};

/**
 * Helper to reset query client state (useful for logout)
 *
 * @example
 * ```tsx
 * // On logout
 * resetQueryClient();
 * ```
 */
export const resetQueryClient = () => {
  queryClient.clear();
};

export default queryClient;
